import {Component, EventEmitter, Input, OnInit, Output} from '@angular/core';
import {forkJoin, of} from 'rxjs';
import {catchError, map} from 'rxjs/operators';
import moment from 'moment';
import 'moment/locale/es';
import {TerceroService} from '../../../../../_services/tercero.service';
import {TerceroTelefonoService} from '../../../../../_services/tercero-telefono.service';
import {SolicitudUnidadCreditoService} from '../../../../../_services/solicitud-unidad-credito.service';
import {TerceroUpPasarelaProductoService} from '../../../../../_services/tercero-up-pasarela-producto.service';
import {TerceroUPPasarelaProducto} from '../../../../../_models/tercero-up-pasarela-producto';
import {AdmsbMetodoChip, AdmsbTitularVista} from '../../../../../_models/admsb-titular';
import {ADMSB_COLOR_POR_METODO, extraerCelular} from '../../admsb-titular.helpers';

@Component({
  selector: 'app-admsb-encabezado',
  templateUrl: './admsb-encabezado.component.html',
  styleUrls: ['./admsb-encabezado.component.scss'],
})
export class AdmsbEncabezadoComponent implements OnInit {

  /** Identificador del tercero titular de la pasarela */
  @Input() idTercero: string;

  /** Identificador de la pasarela del titular (idTerceroUPPasarela) */
  @Input() idTitular: string;

  /** Solicitud de unidad de crédito del titular, usada para validar si tiene billetera */
  @Input() idSolicitudUnidadCredito: string;

  /** Emite los datos del titular una vez cargados, para las secciones que los necesitan */
  @Output() titularCargado = new EventEmitter<AdmsbTitularVista>();

  cargandoTitular = true;

  titular: AdmsbTitularVista = {
    nombreCompleto: '',
    fechaCreacion: '',
    numeroDocumento: '',
    telefono: '',
    correo: '',
    ocupacion: '',
    actividadEconomica: '',
    usuario: '',
    metodosHabilitados: [],
  };

  constructor(
    private terceroServicio: TerceroService,
    private terceroTelefonoServicio: TerceroTelefonoService,
    private solUnidadCreditoServicio: SolicitudUnidadCreditoService,
    private terceroUpPasarelaProductoServicio: TerceroUpPasarelaProductoService,
  ) {
  }

  ngOnInit(): void {
    if (this.idTercero) {
      this.cargarTitular(this.idTercero);
    } else {
      this.cargandoTitular = false;
    }
  }

  private cargarTitular(idTercero: string): void {
    forkJoin([
      this.terceroServicio.get(idTercero),
      this.terceroTelefonoServicio.listadoPorTercero(idTercero),
      this.validarBilletera(),
      this.cargarMetodos(),
    ]).subscribe({
      next: ([t, telefonos, tieneBilletera, metodosHabilitados]) => {
        this.titular = {
          nombreCompleto: t.nombreCompleto,
          fechaCreacion: t.fechaCreacion ? moment(t.fechaCreacion).format('DD-MMM-YYYY') : '',
          numeroDocumento: t.numeroDocumento,
          telefono: extraerCelular(telefonos),
          correo: t.correoElectronico,
          ocupacion: t.ocupacion,
          actividadEconomica: t.actividadEconomica,
          usuario: tieneBilletera ? 'Billetera' : '',
          metodosHabilitados,
        };
        this.cargandoTitular = false;
        this.titularCargado.emit(this.titular);
      },
      error: () => this.cargandoTitular = false,
    });
  }

  /**
   * Valida si el titular tiene billetera.
   *
   * @returns `true` si el titular tiene billetera; `false` si no la tiene o si no hay solicitud.
   */
  private validarBilletera() {
    if (!this.idSolicitudUnidadCredito) {
      return of(false);
    }
    return this.solUnidadCreditoServicio.validarBilleteraMosi(this.idSolicitudUnidadCredito).pipe(
      catchError(() => of(false)),
    );
  }

  /**
   * Obtiene los métodos de pasarela habilitados del titular para pintarlos como chips.
   *
   * @returns Chips de los métodos habilitados; lista vacía si no hay pasarela o falla la consulta.
   */
  private cargarMetodos() {
    if (!this.idTitular) {
      return of([] as AdmsbMetodoChip[]);
    }
    return this.terceroUpPasarelaProductoServicio.listMetodosPasarela(this.idTitular).pipe(
      map((productos: TerceroUPPasarelaProducto[]) => productos
        .filter(p => p.indicadorHabilitado)
        .map(p => {
          const nombre = p.nombreMetodoLista || p.codigoMetodoLista;
          return {nombre, colorClase: ADMSB_COLOR_POR_METODO[nombre] ?? 'admsb-chip--gris'};
        })),
      catchError(() => of([] as AdmsbMetodoChip[])),
    );
  }

}
